import { DownloadOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Dropdown, Image } from 'antd';
import { useTranslation } from 'next-i18next';
import { memo, useCallback, useMemo, useState } from 'react';

import PostVideo from '@/components/web-viewer-for-instagram/PostVideo';
import useCheckIsMobile from '@/components/web-viewer-for-instagram/useCheckIsMobile';
import { useUserProvider } from '@/context/UserProvider';
import { cn } from '@/lib/utils';
import { EventName } from '@/shared/event-types';
import { ga_logEvent } from '@/shared/ga';
import { ThreadsMediaItemModelV1, ThreadsPostItemModelV1, ThreadsUserModelV1 } from '@/types/threads';
import { fallbackImage, imageProxyUrl, mediaDownload, PlayIcon } from '../shared';
import ThreadsPostHeader from '../ThreadsPostHeader';
import ThreadsPostNoMedia from '../ThreadsPostNoMedia';

interface ThreadsPostContentProps {
  post: ThreadsPostItemModelV1;
  user: ThreadsUserModelV1;
}

const ThreadsPostContent: React.FC<ThreadsPostContentProps> = ({ post, user }) => {
  const { t } = useTranslation();
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const { isMobile } = useCheckIsMobile();
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(0);
  const mediaList = useMemo(() => post?.media_list || [], [post]);

  const handleDownloadMedia = useCallback(
    (media: ThreadsMediaItemModelV1) => {
      ga_logEvent(EventName.threads_downloader_dl_btn_clicked);

      if (!isLogin) {
        onChangedLoginView(false);
        onOpenLoginModal(true);
        ga_logEvent(EventName.threads_downloader_dl_btn_c_to_login);
        return;
      }

      ga_logEvent(EventName.threads_downloader_dl_btn_c_to_download);
      mediaDownload(media.url, media.media_type);
    },
    [isLogin, onChangedLoginView, onOpenLoginModal]
  );

  const renderResolutionBadge = useCallback((media: ThreadsMediaItemModelV1) => {
    if (!media.width || !media.height) return null;

    return (
      <>
        <span className="text-xs text-[var(--app-primary-color)] bg-[#ECE9FF] px-1.5 py-0.5 rounded-sm">
          {media.width}x{media.height}
        </span>
        {media.width > 1024 && media.height > 1024 && (
          <span className="w-[30px] h-[20px] rounded-sm text-xs text-center leading-5 text-white bg-[#FF29C2]">HD</span>
        )}
      </>
    );
  }, []);

  const renderDownloadButton = useCallback(
    (media: ThreadsMediaItemModelV1, version?: ThreadsMediaItemModelV1, className?: string) => {
      const targetMedia = version || media;
      const isVideo = targetMedia.media_type === 2;

      return (
        <div
          onClick={() => handleDownloadMedia(targetMedia)}
          className={
            className ||
            cn([
              'bg-gray-100 border border-gray-200 text-gray-800 px-3 py-1.5 rounded-md text-sm flex justify-center items-center gap-1.5 cursor-pointer',
              'hover:border-[var(--app-primary-color)] hover:text-[var(--app-primary-color)] hover:shadow-[0px_2px_6px_0px_rgba(90,52,146,0.08)] transition-colors duration-300',
            ])
          }
        >
          <DownloadOutlined className="mr-1" />
          <span>
            {t('Download')} {isVideo ? t('Video') : t('Image')}
          </span>
          {renderResolutionBadge(targetMedia)}
        </div>
      );
    },
    [handleDownloadMedia, renderResolutionBadge, t]
  );

  // 渲染媒体下载按钮列表（用于下方主按钮区域）
  const renderMediaDownloadButton = useCallback(
    (media: ThreadsMediaItemModelV1) => {
      const versions = media.version_medias || [];

      return (
        <div className={'flex flex-col gap-2.5'}>
          {!versions?.length && renderDownloadButton(media)}
          {versions.length > 0 && versions.map((version) => renderDownloadButton(media, version))}
        </div>
      );
    },
    [renderDownloadButton]
  );

  // 渲染下拉菜单（用于预览时显示）
  const renderDownloadDropdown = useCallback(
    (media: ThreadsMediaItemModelV1) => {
      const isVideo = media.media_type === 2;
      const versions = media.version_medias || [];

      const menuItems =
        versions.length > 0
          ? versions.map((version, index) => ({
              key: `version-${index}`,
              label: (
                <div className="flex justify-center items-center gap-2">
                  <DownloadOutlined className="mr-1" />
                  <span>
                    {t('Download')} {isVideo ? t('Video') : t('Image')}
                  </span>
                  {renderResolutionBadge(version)}
                </div>
              ),
              onClick: () => handleDownloadMedia(version),
            }))
          : [
              {
                key: 'original',
                label: (
                  <div className="flex justify-center items-center gap-2">
                    <DownloadOutlined className="mr-1" />
                    <span>
                      {t('Download')} {isVideo ? t('Video') : t('Image')}
                    </span>
                    {renderResolutionBadge(media)}
                  </div>
                ),
                onClick: () => handleDownloadMedia(media),
              },
            ];

      return (
        <ConfigProvider
          theme={{
            components: {
              Dropdown: {
                zIndexPopup: 2000,
              },
            },
          }}
        >
          <Dropdown
            menu={{ items: menuItems }}
            placement={isMobile ? 'top' : 'topLeft'}
            arrow={{ pointAtCenter: true }}
            trigger={['hover']}
            overlayClassName="download-dropdown-overlay"
          >
            <Button type="primary" className="w-[300px] h-[42px] text-sm hover:!bg-[var(--app-primary-color)]">
              <DownloadOutlined className="mr-1" />
              <span>
                {t('Download')} {isVideo ? t('Video') : t('Image')}
              </span>
            </Button>
          </Dropdown>
        </ConfigProvider>
      );
    },
    [handleDownloadMedia, isMobile, renderResolutionBadge, t]
  );

  const renderMediaPreview = useCallback(
    (media: ThreadsMediaItemModelV1) => {
      const isVideo = media.media_type === 2;

      return (
        <div className="relative flex flex-col items-center justify-center gap-9 max-md:gap-4">
          <div className="w-[1080px] h-[607px] flex flex-col items-center justify-center max-h-[70vh] max-md:w-full max-md:max-h-[70vh] max-md:px-2">
            {isVideo ? (
              <PostVideo
                src={media.url}
                poster={imageProxyUrl(media.cover_image)}
                isShowVedioControls={false}
                loop={true}
                rootClassName="max-h-full"
              />
            ) : (
              <Image
                src={imageProxyUrl(media.url)}
                alt="Image"
                height={607}
                preview={false}
                className="w-auto object-contain"
                fallback={fallbackImage}
              />
            )}
          </div>
          {renderDownloadDropdown(media)}
        </div>
      );
    },
    [renderDownloadDropdown]
  );

  const renderMediaCard = useCallback(
    (media: ThreadsMediaItemModelV1, index: number) => {
      const isVideo = media.media_type === 2;

      const handlePreviewClick = () => {
        setVisible(true);
        setCurrent(index);
      };

      return (
        <div key={`media-${index}`} className="flex flex-col gap-2.5">
          <div className="rounded-md bg-gray-50 overflow-hidden relative group">
            <div className="relative flex items-center justify-center" onClick={handlePreviewClick}>
              <div className="w-[300px] h-[300px] cursor-pointer max-md:w-full">
                <Image
                  src={imageProxyUrl(isVideo ? media.cover_image : media.url || post.cover_image)}
                  alt={isVideo ? 'Video' : post.caption_text || 'Image'}
                  className="w-full h-full object-cover"
                  width={300}
                  height={300}
                  wrapperStyle={{
                    width: '100%',
                    height: '100%',
                  }}
                  preview={{
                    mask: null,
                    imageRender: () => renderMediaPreview(media),
                    toolbarRender: () => undefined,
                  }}
                  fallback={fallbackImage}
                />
              </div>
              <div className="absolute top-2 right-2 max-md:top-3 max-md:left-3 max-md:right-auto bg-gray-800 bg-opacity-70 text-white text-base px-2 py-1 rounded">
                {isVideo ? 'Video' : 'Image'}
              </div>
              {isVideo && (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-[#0000004D] cursor-pointer">
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <PlayIcon />
                  </div>
                </div>
              )}
            </div>
          </div>
          {renderMediaDownloadButton(media)}
        </div>
      );
    },
    [post, renderMediaDownloadButton, renderMediaPreview]
  );

  if (!post) return null;

  return (
    <div className="p-8 border border-[#E5E5E5ED] rounded-xl shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)] overflow-hidden max-md:px-[18px] max-md:py-6">
      <ThreadsPostHeader mediaName={user.username} post={post} />

      {mediaList.length === 0 ? (
        <ThreadsPostNoMedia />
      ) : (
        <div className="grid grid-cols-3 gap-x-2.5 gap-y-8 max-md:grid-cols-1 max-md:gap-y-6">
          <Image.PreviewGroup
            items={mediaList.map((item) => ({ src: JSON.stringify(item) }))}
            preview={{
              visible,
              onVisibleChange: setVisible,
              current,
              onChange: (current, prevCurrent) => {
                if (current !== prevCurrent) {
                  setCurrent(current);
                }
              },
              imageRender: (originalNode, info: any) => {
                if (!info?.image?.url) return null;
                return renderMediaPreview(JSON.parse(info.image.url));
              },
              toolbarRender: () => undefined,
            }}
          >
            {mediaList.map(renderMediaCard)}
          </Image.PreviewGroup>
        </div>
      )}
    </div>
  );
};

export default memo(ThreadsPostContent);
